import { BufferAttribute, CapsuleGeometry, Color, InstancedMesh, Object3D, ShaderMaterial, SphereGeometry, UniformsLib, UniformsUtils } from 'three';
import type { BufferGeometry } from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { crowdBeat, crowdExcitement, flashBurst, sceneTime } from './time';

const shirts = [0xc0392b, 0x2e5fb8, 0xe8e8e0, 0x2c2c34, 0x3f8f5a, 0xe0a33a, 0x7a3fa0, 0x1f7a8c, 0x8a8a90, 0xd0643a, 0x1b1b1f, 0xf2d64b];
const parts = { body: 0, head: 1, leftArm: 2, rightArm: 3 };
/** Where the arms hang from, in a spectator's own space. */
const shoulderHeight = 0.9;
const shoulderWidth = 0.23;

// Each part of a spectator marked, so the shader can colour the head and swing the arms.
function marked(geometry: BufferGeometry, part: number): BufferGeometry {
  const flat = geometry.toNonIndexed();
  flat.setAttribute('part', new BufferAttribute(new Float32Array(flat.attributes.position.count).fill(part), 1));
  return flat;
}

/** A spectator: a body, a head, and two arms hanging from the shoulders. Far away in the dark, a few facets are enough. */
function person(): BufferGeometry {
  const body = new CapsuleGeometry(0.2, 0.42, 2, 8);
  body.translate(0, 0.5, 0);
  const head = new SphereGeometry(0.13, 8, 6);
  head.translate(0, 1.03, 0);
  const arms = [-1, 1].map((side) => {
    const arm = new CapsuleGeometry(0.05, 0.46, 1, 4);
    // Hanging straight down from the shoulder; the shader swings it up from there.
    arm.translate(side * shoulderWidth, shoulderHeight - 0.28, 0);
    return marked(arm, side < 0 ? parts.leftArm : parts.rightArm);
  });
  return mergeGeometries([marked(body, parts.body), marked(head, parts.head), ...arms])!;
}

const vertexShader = /* glsl */ `
  attribute float part;
  uniform float time;
  uniform float excitement;
  uniform float beat;
  varying vec3 vWorld;
  varying vec3 vNormal;
  varying vec3 vColour;
  varying float vPart;
  varying float vSeed;
  #include <fog_pars_vertex>

  // Turns a point about the spectator's forward axis, round a pivot.
  vec3 swing(vec3 p, vec3 pivot, float angle) {
    vec3 q = p - pivot;
    float c = cos(angle), s = sin(angle);
    return pivot + vec3(c * q.x - s * q.y, s * q.x + c * q.y, q.z);
  }

  void main() {
    float seed = fract(sin(float(gl_InstanceID) * 12.9898) * 43758.5453);
    vSeed = seed;
    vPart = part;
    vec3 p = position;
    vec3 n = normal;
    // Arms: most hang loose; the more the hall is roused, the more of them go up, and those up wave.
    if (part > 1.5) {
      float side = part < 2.5 ? -1.0 : 1.0;
      float up = clamp(excitement * 1.6 - seed * 1.1 + step(0.8, seed) * 0.4, 0.0, 1.0);
      float wave = sin(time * (5.0 + seed * 3.0) + seed * 20.0) * 0.3 * up;
      float angle = side * (0.12 + up * 2.55 + wave);
      vec3 pivot = vec3(side * ${shoulderWidth.toFixed(3)}, ${shoulderHeight.toFixed(3)}, 0.0);
      p = swing(p, pivot, angle);
      n = swing(n, vec3(0.0), angle);
    }
    // Everyone bounces in their own rhythm, higher when the hall is on its feet.
    p.y += abs(sin(time * (4.0 + seed * 3.0) + beat + seed * 6.2831)) * (0.02 + 0.06 * step(0.4, seed)) * (1.0 + excitement * 2.5);
    vec4 world = modelMatrix * instanceMatrix * vec4(p, 1.0);
    vWorld = world.xyz;
    vNormal = normalize(mat3(modelMatrix) * mat3(instanceMatrix) * n);
    vColour = instanceColor;
    vec4 mvPosition = viewMatrix * world;
    gl_Position = projectionMatrix * mvPosition;
    #include <fog_vertex>
  }`;

const fragmentShader = /* glsl */ `
  uniform float time;
  uniform float burst;
  varying vec3 vWorld;
  varying vec3 vNormal;
  varying vec3 vColour;
  varying float vPart;
  varying float vSeed;
  #include <fog_pars_fragment>

  void main() {
    vec3 n = normalize(vNormal);
    // Warm spill from the ring's lights, bright on the rows by the ring and fading up the stands.
    vec3 toRing = normalize(vec3(0.0, 3.0, 0.0) - vWorld);
    float reach = exp(-max(length(vWorld.xz) - 6.5, 0.0) * 0.21);
    float facing = max(dot(n, toRing), 0.0) * 0.8 + 0.2;
    vec3 light = vec3(1.0, 0.8, 0.6) * facing * reach * 0.5;
    // A cool edge from the house lights, so heads and shoulders stand out against the dark behind them.
    float rim = pow(1.0 - abs(dot(n, normalize(cameraPosition - vWorld))), 3.0);
    light += vec3(0.3, 0.42, 0.85) * rim * (0.12 + 0.18 * max(n.y, 0.0));
    light += vec3(0.015, 0.017, 0.025);
    // When the flashes go off, faces all over the hall catch them.
    float flash = step(0.82, fract(vSeed * 37.0 + floor(time * 14.0) * 0.618));
    light += vec3(1.3) * burst * flash * facing;

    vec3 skin = mix(vec3(0.95, 0.76, 0.62), vec3(0.36, 0.23, 0.16), fract(vSeed * 7.13));
    vec3 colour = vPart > 0.5 && vPart < 1.5 ? skin : vColour;
    gl_FragColor = vec4(colour * light, 1.0);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
    #include <fog_fragment>
  }`;

export interface Seat {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** Which way they face, in radians. */
  readonly turn: number;
}

/**
 * The crowd on the stands, every spectator in one mesh drawn thousands of times at once. The light on them, their
 * bounce and their arms going up when the hall is roused are all worked out on the graphics card; nothing is moved
 * on the CPU.
 */
export function buildCrowd(seats: readonly Seat[]): InstancedMesh {
  const random = mulberry(7);
  const material = new ShaderMaterial({
    uniforms: UniformsUtils.merge([UniformsLib.fog, { time: { value: 0 }, excitement: { value: 0 }, beat: { value: 0 }, burst: { value: 0 } }]),
    vertexShader,
    fragmentShader,
    fog: true,
  });
  // Shared with the rest of the scene, so they move with it.
  material.uniforms.time = sceneTime;
  material.uniforms.excitement = crowdExcitement;
  material.uniforms.beat = crowdBeat;
  material.uniforms.burst = flashBurst;
  const mesh = new InstancedMesh(person(), material, seats.length);
  const place = new Object3D();
  const colour = new Color();
  seats.forEach((seat, index) => {
    place.position.set(seat.x, seat.y, seat.z);
    place.rotation.set(0, seat.turn + (random() - 0.5) * 0.4, 0);
    const scale = 0.9 + random() * 0.25;
    place.scale.set(scale, scale, scale);
    place.updateMatrix();
    mesh.setMatrixAt(index, place.matrix);
    mesh.setColorAt(index, colour.setHex(shirts[Math.floor(random() * shirts.length)]).multiplyScalar(0.55 + random() * 0.45));
  });
  mesh.frustumCulled = false;
  mesh.name = 'Crowd';
  return mesh;
}

/** Numbers 0..1 from a seed, so the crowd sits the same way every time. */
export function mulberry(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
